import React from "react";
import { Tag, Dropdown, Button } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import tickImg from "../images/icons/tick.png";
import { useCart } from "../contexts/CartContext";

interface Props {
  id: number,
  title: string;
  price: number;
  questionCount: number;
  timeLimit: string;
  description: string;
  industryTags: string[];
  industryColor: string;
  showCart: boolean;
  inCart?: boolean;
  onClick?: () => void;
  status?: "ready" | "in-progress" | "completed" | null;
  onViewResults?: () => void;
  onViewApplicants?: () => void;
  onDownloadCertificate?: () => void;
  isAdminView?: boolean;
  inviterName?: string | null;
  inviterEmail?: string | null;
  invitedAt?: string;
  completedAt?: string | null;
}

const CandidateTestCard: React.FC<Props> = ({
  id,
  title,
  price,
  questionCount,
  timeLimit,
  description,
  industryTags,
  industryColor,
  showCart,
  inCart,
  onClick,
  status,
  onViewResults,
  onViewApplicants,
  onDownloadCertificate,
  isAdminView,
  inviterName,
  inviterEmail,
  invitedAt,
  completedAt
}) => {
  const { addItem, removeItem, isInCart } = useCart();

  const getInvitationInfo = () => {
    if (!invitedAt) return null;

    const invitedDate = new Date(invitedAt);
    const formattedDate = invitedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    if (!inviterName && !inviterEmail) {
      // Self-purchased assessment
      return `Acquired on ${formattedDate}`;
    } else {
      // Invited by business/admin
      const inviterDisplay = inviterName || inviterEmail;
      return `Invited by ${inviterDisplay}`;
    }
  };

  const getStatusTag = () => {
    if (!status) return null;

    const isCompleted = status === "completed";
    const isInProgress = status === "in-progress";
    return (
      <Tag style={{
        ...statusTagStyle,
        background: isCompleted ? "#f6ffed" : isInProgress ? "#fffbe6" : "#fff2e8",
        color: isCompleted ? "#52c41a" : isInProgress ? "#d48806" : "#fa8c16",
        border: `1px solid ${isCompleted ? "#b7eb8f" : isInProgress ? "#ffe58f" : "#ffd591"}`,
      }}>
        {isCompleted ? "Completed" : isInProgress ? "In Progress" : "Ready to take"}
      </Tag>
    );
  };

  const formatTime = (time: string) => {
    let limit = "";

    if (time.trim() == "") {
      limit = "No"
    } else {
      limit = time + ":00"
    }

    limit += " time limit"
    return limit;
  }

  const cartToggle = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (inCart) {
      removeItem(id);
    } else {
      addItem({
        id: id,
        name: title,
        price: price,
        industry_name: industryTags[0],
        industry_color: industryColor,
      });
    }

    inCart = !inCart;
  }

  const getDropdownMenu = () => {
    // For admin view, always show the menu
    if (isAdminView) {
      const menuItems = [
        {
          key: 'results',
          label: 'View questions',
          onClick: (e: any) => {
            e.domEvent.stopPropagation();
            onViewResults?.();
          }
        },
        {
          key: 'applicants',
          label: 'View applicants',
          onClick: (e: any) => {
            e.domEvent.stopPropagation();
            onViewApplicants?.();
          }
        },
        {
          key: 'certificate',
          label: 'Send to...',
          onClick: (e: any) => {
            e.domEvent.stopPropagation();
            onDownloadCertificate?.();
          }
        }
      ];

      return (
        <Dropdown
          menu={{ items: menuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<EllipsisOutlined style={{ fontSize: 16 }} />}
            style={menuButtonStyle}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      );
    }

    // For regular candidate view, only show menu if completed
    if (status !== "completed") return null;

    const menuItems = [
      {
        key: 'results',
        label: 'View results',
        onClick: (e: any) => {
          e.domEvent.stopPropagation();
          onViewResults?.();
        }
      },
      {
        key: 'certificate',
        label: 'View PPP',
        onClick: (e: any) => {
          e.domEvent.stopPropagation();
          onDownloadCertificate?.();
        }
      }
    ];

    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<EllipsisOutlined style={{ fontSize: 16 }} />}
          style={menuButtonStyle}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    );
  };

  return (
    <div style={{
      ...card,
      ...(inCart ? { border: "solid 2px #0051FF" } : {})
    }}
      onClick={onClick}>
      {/* Status tag and menu */}
      {getStatusTag() || getDropdownMenu() ? (
        <div style={headerContainer}>
          <div style={tagsContainer}>
            {getStatusTag()}
          </div>
          {getDropdownMenu()}
        </div>
      ) : null}

      {/* Title */}
      <div style={titleContainer}>
        <h3 style={titleStyle}>{title}</h3>

        {showCart ?
          <div onClick={cartToggle} style={{
            ...cartCircle,
            ...(inCart ? { backgroundColor: "#0051FF", border: "none" } : {})
          }}>
            <img src={tickImg} style={tickStyle} />
          </div>
          : null}
      </div>

      {/* Question count and time limit */}
      <div style={metaContainer}>
        <span style={metaText}>{questionCount} {questionCount === 1 ? 'question' : 'questions'}</span>
        <span style={metaText}>{formatTime(timeLimit)}</span>
      </div>

      {/* Description */}
      <p style={descriptionStyle}>
        {description}
      </p>

      {/* Industry tags */}
      <div style={tagsContainer}>
        {industryTags.map((tag, index) => (
          <Tag key={index} style={tagStyle}>
            {tag}
          </Tag>
        ))}
      </div>

      {/* Invitation information */}
      {getInvitationInfo() && (
        <div style={invitationInfoStyle}>
          {getInvitationInfo()}
        </div>
      )}
      {completedAt && (
        <div style={invitationInfoStyle}>
          Completed {new Date(completedAt).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })} at {new Date(completedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
};

const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 18,
  padding: 24,
  border: "1px solid #f0f0f0",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: "#1a1a1a",
  margin: "0 0 12px 0",
  lineHeight: "1.3",
};

const metaContainer: React.CSSProperties = {
  display: "flex",
  gap: "7px 24px",
  marginBottom: "16px",
  flexWrap: "wrap"
};

const metaText: React.CSSProperties = {
  fontSize: 12,
  color: "#666",
  fontWeight: 500,
  border: "1px solid #E6EBFA",
  padding: "0px 15px",
  borderRadius: "5px"
};

const descriptionStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#666",
  lineHeight: "1.5",
  margin: "0 0 20px 0",
  flex: 1,
};

const tagsContainer: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "auto",
};

const tagStyle: React.CSSProperties = {
  background: "#F4F8FF",
  color: "#8E99BA",
  border: "none",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  padding: "0px 15px",
  margin: 0,
  lineHeight: "1.2rem"
};

const statusTagStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 10,
  margin: 0,
  padding: "4px 12px",
  marginBottom: "10px"
};

const headerContainer: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "10px",
};

const menuButtonStyle: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "none",
  color: "#666",
  minWidth: "auto",
  height: "auto",
};

const cartCircle: React.CSSProperties = {
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  maxWidth: 24,
  maxHeight: 24,
  border: "solid 1px #E6EBFA",
  borderRadius: "50%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const titleContainer: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between"
}

const tickStyle: React.CSSProperties = {
  height: 12,
  width: 15
}

const invitationInfoStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#999",
  marginTop: 12,
  fontWeight: 400,
  fontStyle: 'italic'
};

export default CandidateTestCard;